import {Button, Input, Select, Spin} from "antd";
import {Header} from "antd/es/layout/layout";
import React, {useEffect, useMemo, useState} from "react";
import {MenuFoldOutlined, MenuUnfoldOutlined} from "@ant-design/icons";
import styles from "./index.module.scss";
import {LOCALES} from "../../../i18n";
import {useDispatch, useSelector} from "react-redux";
import {changeLang} from "../../../slice/lang";
import translate from "../../../i18n/translate";
import {useQuery} from "react-query";
import jwtAxios from "../../../auth/jwt-api";
import {changeRate} from "../../../slice/dollar";
import debounce from "lodash/debounce";
import {attachUser, clearAttach} from "../../../slice/userData";


const {Option} = Select;
export default function CRMNavbar({collapsed, setCollapsed}) {
    const dispatch = useDispatch()
    const {data, refetch} = useQuery(
        "/dollar-rate/current",
        () => jwtAxios.get("/dollar-rate/current")
    );
    useEffect(() => {
        dispatch(changeRate(Number(data?.data?.data?.rate)))
    }, [data?.data]);

    return (
        <Header className={styles.header}>
            {setCollapsed && (
                <Button
                    type="text"
                    icon={collapsed ? <MenuUnfoldOutlined/> : <MenuFoldOutlined/>}
                    onClick={() => setCollapsed(!collapsed)}
                    className={styles.button}
                />
            )}
            <img src={"/logo.png"} alt=""/>

            <BitrixSearch/>

            <LanguageSwitcher/>
        </Header>
    );
}

function LanguageSwitcher() {
    const {lang} = useSelector(state => state.lang)
    const dispatch = useDispatch()
    const changeLanguage = (value) => {
        dispatch(changeLang(value))
    }
    return (
        <Select
            value={lang}
            style={{width: 140}}
            onChange={(value) => changeLanguage(value)}
        >
            <Option value={LOCALES.UZBEK}>🇺🇿 {translate("language.uzbek")}</Option>
            <Option value={LOCALES.RUSSIAN}>🇷🇺 {translate("language.russian")}</Option>
        </Select>
    );
}

const countryCodes = [
    {code: "+998", flag: "🇺🇿", country: "UZ", format: "## ### ## ##", placeholder: "90 123 45 67"},
    {code: "+7", flag: "🇷🇺", country: "RU", format: "### ### ## ##", placeholder: "999 123 45 67"},
    {code: "+1", flag: "🇺🇸", country: "US", format: "### ### ####", placeholder: "555 123 4567"},
    {code: "+44", flag: "🇬🇧", country: "GB", format: "#### ### ###", placeholder: "7911 123 456"},
    {code: "+49", flag: "🇩🇪", country: "DE", format: "### ### ####", placeholder: "151 123 4567"},
    {code: "+90", flag: "🇹🇷", country: "TR", format: "### ### ## ##", placeholder: "532 123 45 67"},
    {code: "+82", flag: "🇰🇷", country: "KR", format: "## #### ####", placeholder: "10 1234 5678"},
    {code: "+86", flag: "🇨🇳", country: "CN", format: "### #### ####", placeholder: "131 1234 5678"},
    {code: "+971", flag: "🇦🇪", country: "AE", format: "## ### ####", placeholder: "50 123 4567"},
    {code: "+7", flag: "🇰🇿", country: "KZ", format: "### ### ## ##", placeholder: "701 123 45 67"},
    {code: "+992", flag: "🇹🇯", country: "TJ", format: "## ### ## ##", placeholder: "90 123 45 67"},
    {code: "+93", flag: "🇦🇫", country: "AF", format: "## ### ####", placeholder: "70 123 4567"},
    {code: "+996", flag: "🇰🇬", country: "KG", format: "### ### ###", placeholder: "700 123 456"},
    {code: "+993", flag: "🇹🇲", country: "TM", format: "## ## ## ##", placeholder: "65 12 34 56"},
];

const formatPhoneByPattern = (value, pattern) => {
    const digits = value.replace(/\D/g, "");
    let result = "";
    let digitIndex = 0;

    for (let i = 0; i < pattern.length && digitIndex < digits.length; i++) {
        if (pattern[i] === "#") {
            result += digits[digitIndex];
            digitIndex++;
        } else {
            result += pattern[i];
        }
    }

    return result;
};

function BitrixSearch() {
    const {userData} = useSelector(state => state.userData);
    const [value, setValue] = useState("");
    const [countryCode, setCountryCode] = useState("+998");
    const [searchValue, setSearchValue] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchType, setSearchType] = useState("phone"); // "phone" or "pinfl"
    const dispatch = useDispatch()

    useEffect(() => {
        if (userData?.id) {
            setValue(userData.id);
            setSearchValue(`${userData.customerName || ""} — ${userData.customerPhone || ""}`);
        }
    }, [userData?.id]);
    const searchCustomers = async (phone, code, type) => {
        const clean = phone.replace(/\D/g, "");

        // For PINFL/INN search, require at least 5 characters
        // For phone search, require at least 7 digits
        const minLength = type === "pinfl" ? 5 : 7;
        if (clean.length < minLength) {
            setOptions([]);
            return;
        }

        setLoading(true);
        try {
            const params = type === "pinfl"
                ? { pinfl: clean }
                : { customerPhone: phone, countryCode: code };

            const res = await jwtAxios.get("/client/search-by-phone", { params });

            const customers = res?.data?.data || [];
            setOptions(
                customers?.clients?.map((c) => ({
                    value: customers?.source === "database" ? c.id : c.ID,
                    label: customers?.source === "database"
                        ? `${c.customerName || ""} — ${c.customerPhone}`
                        : `${c.NAME || ""} ${c.LAST_NAME || ""} — ${c.PHONE?.[0]?.VALUE}`,
                    c: c,
                    source: customers?.source,
                }))
            );
        } catch (e) {
            console.error(e);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    const debouncedSearch = useMemo(
        () => debounce(searchCustomers, 1000),
        []
    );

    const handleSearch = (val) => {
        if (value) return;

        if (searchType === "pinfl") {
            // For PINFL/INN, just allow digits without formatting
            const digitsOnly = val.replace(/\D/g, "");
            setSearchValue(digitsOnly);
            setOptions([]);
            debouncedSearch(digitsOnly, countryCode, searchType);
        } else {
            const selectedCountry = countryCodes.find(c => c.code === countryCode);
            const formatted = formatPhoneByPattern(val, selectedCountry?.format || "## ### ## ##");
            setSearchValue(formatted);
            setOptions([]);
            debouncedSearch(val, countryCode, searchType);
        }
    };

    const handleSearchTypeChange = (type) => {
        setSearchType(type);
        setSearchValue("");
        setOptions([]);
        setValue(null);
        dispatch(clearAttach());
    };

    const handleSelect = async (selectedValue, option) => {
        setValue(selectedValue);
        setSearchValue(option.label);

        if (option.source === "database") {
            dispatch(attachUser({
                id: option?.c?.id,
                customerName: option?.c?.customerName || "",
                customerPhone: option?.c?.customerPhone || "",
                customerPhone2: option?.c?.customerPhone2 || "",
                email: option?.c?.email || "",
                address: option?.c?.address || "",
                clientType: option?.c?.clientType || "individual",
                // Individual fields
                passportGivenPlace: option?.c?.passportGivenPlace || "",
                passportGivenDate: option?.c?.passportGivenDate || "",
                series: option?.c?.series || "",
                pnfl: option?.c?.pnfl || "",
                birthDate: option?.c?.birthDate || "",
                // Legal fields
                director: option?.c?.director || "",
                bank: option?.c?.bank || "",
                bank_account: option?.c?.bank_account || "",
                mfo_code: option?.c?.mfo_code || "",
                oked_code: option?.c?.oked_code || "",
                inn: option?.c?.inn || "",
                favorites: option?.c?.favorites || []
            }));
        } else if (option.source === "bitrix") {
            setLoading(true);
            try {
                const res = await jwtAxios.post("/client/create-from-bitrix", {
                    bitrixId: selectedValue
                });
                const client = res?.data?.data?.client;
                dispatch(attachUser({
                    id: client?.id,
                    customerName: client?.customerName || "",
                    customerPhone: client?.customerPhone || "",
                    customerPhone2: client?.customerPhone2 || "",
                    email: client?.email || "",
                    address: client?.address || "",
                    clientType: client?.clientType || "individual",
                    // Individual fields
                    passportGivenPlace: client?.passportGivenPlace || "",
                    passportGivenDate: client?.passportGivenDate || "",
                    series: client?.series || "",
                    pnfl: client?.pnfl || "",
                    birthDate: client?.birthDate || "",
                    // Legal fields
                    director: client?.director || "",
                    bank: client?.bank || "",
                    bank_account: client?.bank_account || "",
                    mfo_code: client?.mfo_code || "",
                    oked_code: client?.oked_code || "",
                    inn: client?.inn || "",
                    favorites: client?.favorites || []
                }));
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        }
    };
    const handleClear = () => {
        setValue(null);
        setSearchValue("");
        setOptions([]);
        dispatch(clearAttach())
    };
    const selectedCountry = countryCodes.find(c => c.code === countryCode);

    return (
        <Input.Group compact style={{display: 'flex', width: 500}}>
            <Select
                value={searchType}
                onChange={handleSearchTypeChange}
                style={{width: 150}}
            >
                <Option value="phone">По телефону</Option>
                <Option value="pinfl">По ПИНФЛ или ИНН</Option>
            </Select>
            {searchType === "phone" && (
                <Select
                    value={countryCode}
                    onChange={(val) => {
                        setCountryCode(val);
                        setSearchValue("");
                        setOptions([]);
                    }}
                    style={{width: 100}}
                    optionLabelProp="label"
                >
                    {countryCodes.map((c) => (
                        <Option key={c.code} value={c.code} label={`${c.flag} ${c.code}`}>
                            {c.flag} {c.code} ({c.country})
                        </Option>
                    ))}
                </Select>
            )}
            <Select
                showSearch
                allowClear
                style={{flex: 1}}
                placeholder={searchType === "pinfl" ? "Введите ПИНФЛ или ИНН" : (selectedCountry?.placeholder || "90 123 45 67")}

                value={value}
                searchValue={searchValue}

                onSearch={handleSearch}
                onSelect={handleSelect}
                onClear={handleClear}

                filterOption={false}
                options={options}

                notFoundContent={loading ? <Spin size="small"/> : null}
            />
        </Input.Group>
    );
}
